/* ==========================================================================
   The Diatomat – customer reviews (no app needed)
   Sources, merged together:
     1. a CSV file uploaded in Content > Files  (Theme settings > The Diatomat)
     2. CSV text pasted in Theme settings > The Diatomat
     3. reviews added as blocks in the "Avis clients" sections (theme editor)
   Renders: star ratings ([data-dm-rating]), review carousels
   ([data-dm-reviews-carousel]) and the full list ([data-dm-reviews-list]).
   ========================================================================== */
(() => {
  const configEl = document.getElementById('dm-reviews-config');
  let config = {};
  try {
    config = JSON.parse(configEl ? configEl.textContent : '{}') || {};
  } catch (e) {
    config = {};
  }
  const LABEL = config.label || 'avis';

  /* ------------------------------------------------------------ helpers */
  const esc = (value) =>
    String(value == null ? '' : value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  const norm = (value) =>
    String(value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]/g, '');

  const fmtRating = (value) => (Math.round(value * 10) / 10).toFixed(1).replace('.', ',');

  const starsHTML = (rating, size = 16) => {
    const pct = Math.max(0, Math.min(100, (rating / 5) * 100));
    const star = (fill) =>
      `<svg viewBox="0 0 20 20" width="${size}" height="${size}" aria-hidden="true"><path fill="${fill}" d="M10 1.6l2.6 5.3 5.8.8-4.2 4.1 1 5.8L10 14.9l-5.2 2.7 1-5.8L1.6 7.7l5.8-.8z"/></svg>`;
    const row = (fill) => `<span class="dm-stars__row">${star(fill).repeat(5)}</span>`;
    return `<span class="dm-stars" role="img" aria-label="${fmtRating(rating)} sur 5"><span class="dm-stars__bg">${row('currentColor')}</span><span class="dm-stars__fg" style="width:${pct}%">${row('currentColor')}</span></span>`;
  };

  /* ---------------------------------------------------------- CSV parse */
  function detectDelimiter(text) {
    const firstLine = text.split(/\r?\n/)[0] || '';
    const counts = { ',': 0, ';': 0, '\t': 0 };
    let inQuotes = false;
    for (const ch of firstLine) {
      if (ch === '"') inQuotes = !inQuotes;
      else if (!inQuotes && ch in counts) counts[ch] += 1;
    }
    return Object.keys(counts).sort((a, b) => counts[b] - counts[a])[0];
  }

  function parseCSV(text) {
    text = text.replace(/^﻿/, '');
    if (!text.trim()) return [];
    const d = detectDelimiter(text);
    const rows = [];
    let row = [];
    let field = '';
    let inQuotes = false;
    for (let i = 0; i < text.length; i += 1) {
      const ch = text[i];
      if (inQuotes) {
        if (ch === '"') {
          if (text[i + 1] === '"') {
            field += '"';
            i += 1;
          } else inQuotes = false;
        } else field += ch;
      } else if (ch === '"') inQuotes = true;
      else if (ch === d) {
        row.push(field);
        field = '';
      } else if (ch === '\n' || ch === '\r') {
        if (ch === '\r' && text[i + 1] === '\n') i += 1;
        row.push(field);
        rows.push(row);
        row = [];
        field = '';
      } else field += ch;
    }
    if (field !== '' || row.length) {
      row.push(field);
      rows.push(row);
    }
    const header = (rows.shift() || []).map(norm);
    return rows
      .filter((r) => r.some((cell) => cell.trim() !== ''))
      .map((r) => {
        const obj = {};
        header.forEach((h, idx) => {
          if (h && obj[h] === undefined) obj[h] = (r[idx] || '').trim();
        });
        return obj;
      });
  }

  const ALIASES = {
    rating: ['rating', 'note', 'notesur5', 'stars', 'etoiles', 'etoile', 'score', 'reviewscore', 'reviewrating', 'rating15'],
    name: ['name', 'nom', 'prenom', 'author', 'auteur', 'reviewer', 'reviewername', 'nickname', 'fullname', 'customername', 'client', 'displayname'],
    title: ['title', 'titre', 'reviewtitle', 'headline', 'sujet'],
    text: ['text', 'texte', 'body', 'review', 'avis', 'content', 'contenu', 'comment', 'commentaire', 'reviewbody', 'reviewcontent', 'message'],
    date: ['date', 'createdat', 'created', 'reviewdate', 'publishedat', 'datedepublication', 'submittedat'],
    product: ['product', 'produit', 'handle', 'producthandle', 'productname', 'productslug'],
    photo: ['photo', 'photos', 'image', 'images', 'img', 'pictureurls', 'pictures', 'mediaurl', 'imageurl', 'reviewimages', 'photourl'],
    verified: ['verified', 'verifie', 'verifiedpurchase', 'verifiedbuyer', 'achatverifie', 'verifiedreviewer'],
    location: ['location', 'ville', 'city', 'pays', 'country', 'lieu'],
    status: ['status', 'statut', 'published', 'publie', 'visible'],
  };

  const pick = (obj, key) => {
    for (const alias of ALIASES[key]) {
      if (obj[alias] !== undefined && obj[alias] !== '') return obj[alias];
    }
    return '';
  };

  const parseRating = (value) => {
    const v = String(value || '').trim();
    if (!v) return 0;
    const stars = (v.match(/★/g) || []).length;
    if (stars) return stars;
    const n = parseFloat(v.replace(',', '.'));
    return Number.isFinite(n) ? Math.max(0, Math.min(5, n)) : 0;
  };

  const parseDate = (value) => {
    const v = String(value || '').trim();
    if (!v) return 0;
    let m = v.match(/^(\d{1,2})[\/.\-](\d{1,2})[\/.\-](\d{2,4})/);
    if (m) {
      const y = m[3].length === 2 ? 2000 + Number(m[3]) : Number(m[3]);
      return new Date(y, Number(m[2]) - 1, Number(m[1])).getTime() || 0;
    }
    const t = Date.parse(v);
    return Number.isFinite(t) ? t : 0;
  };

  const fmtDate = (ts, raw) => {
    if (!ts) return raw || '';
    try {
      return new Date(ts).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
    } catch (e) {
      return raw || '';
    }
  };

  const truthy = (value) => /^(1|true|yes|y|oui|vrai|x|verified|verifie|verifiedbuyer)$/.test(norm(value));

  const photoURLs = (value) =>
    String(value || '')
      .split(/[\s,;|]+/)
      .map((s) => s.trim())
      .filter(Boolean)
      .map((s) => {
        if (/^(https?:)?\/\//i.test(s)) return s;
        if (/\.(jpe?g|png|webp|gif|avif)$/i.test(s)) return '/cdn/shop/files/' + encodeURIComponent(s);
        return '';
      })
      .filter((s) => /^(https?:)?\/\//i.test(s) || s.startsWith('/cdn/'));

  function toReview(row, source) {
    const status = norm(pick(row, 'status'));
    if (['rejected', 'rejete', 'unpublished', 'hidden', 'masque', 'false', 'non', 'no', 'pending'].includes(status)) return null;
    const rating = parseRating(pick(row, 'rating'));
    const name = pick(row, 'name');
    if (!rating) return null;
    if (/^exemple/i.test(name)) return null; // template example rows are ignored
    const rawDate = pick(row, 'date');
    const ts = parseDate(rawDate);
    const verifiedRaw = pick(row, 'verified');
    return {
      rating,
      name: name || 'Client',
      title: pick(row, 'title'),
      text: pick(row, 'text'),
      ts,
      date: fmtDate(ts, rawDate),
      product: pick(row, 'product'),
      photos: photoURLs(pick(row, 'photo')),
      verified: verifiedRaw === '' ? source !== 'manual' : truthy(verifiedRaw),
      location: pick(row, 'location'),
      source,
    };
  }

  /* ------------------------------------------------------------ loading */
  async function fetchCSV(url) {
    if (!url) return '';
    try {
      const res = await fetch(url, { credentials: 'same-origin' });
      if (!res.ok) return '';
      const buf = await res.arrayBuffer();
      let text = new TextDecoder('utf-8').decode(buf);
      if (text.includes('�')) text = new TextDecoder('windows-1252').decode(buf); // Excel "CSV" (non UTF-8)
      return text;
    } catch (e) {
      return '';
    }
  }

  function manualReviews(scope = document) {
    const out = [];
    scope.querySelectorAll('script[data-dm-manual-reviews]').forEach((el) => {
      try {
        JSON.parse(el.textContent).forEach((r) => {
          const review = toReview(r, 'manual');
          if (review) {
            review.counted = el.dataset.dmCount !== 'false';
            out.push(review);
          }
        });
      } catch (e) {
        /* ignore */
      }
    });
    return out;
  }

  const csvPromise = (async () => {
    const [fileText] = await Promise.all([fetchCSV(config.file)]);
    const rows = [...parseCSV(fileText || ''), ...parseCSV(config.inline || '')];
    return rows.map((r) => toReview(r, 'csv')).filter(Boolean);
  })();

  const matchesProduct = (review, handle) => {
    if (!handle || !review.product) return true;
    const p = norm(review.product);
    const h = norm(handle);
    return p === h || p.includes(h) || h.includes(p);
  };

  const stats = (list) => {
    const dist = [0, 0, 0, 0, 0];
    let sum = 0;
    list.forEach((r) => {
      sum += r.rating;
      dist[Math.max(1, Math.min(5, Math.round(r.rating))) - 1] += 1;
    });
    return { count: list.length, avg: list.length ? sum / list.length : 0, dist };
  };

  async function dataset(scopeHandle) {
    const csv = await csvPromise;
    const manual = manualReviews().filter((r) => r.counted);
    return [...csv, ...manual].filter((r) => matchesProduct(r, scopeHandle));
  }

  /* ------------------------------------------------------ star ratings */
  async function renderRatings(scope = document) {
    const els = scope.querySelectorAll('[data-dm-rating]');
    if (!els.length) return;
    for (const el of els) {
      const handle = el.dataset.dmRating === 'all' ? null : el.dataset.dmRating || config.product;
      const list = await dataset(handle);
      const s = stats(list);
      if (!s.count) {
        el.hidden = !el.hasAttribute('data-dm-rating-keep');
        continue;
      }
      const link = el.dataset.dmRatingLink;
      const inner = `${starsHTML(s.avg, Number(el.dataset.dmRatingSize) || 16)}<span class="dm-rating__value">${fmtRating(s.avg)}</span><span class="dm-rating__count">(${s.count} ${esc(LABEL)})</span>`;
      el.innerHTML = link ? `<a href="${esc(link)}" class="dm-rating__link">${inner}</a>` : inner;
      el.hidden = false;
    }
  }

  /* ------------------------------------------------------ review cards */
  const initials = (name) =>
    String(name)
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0].toUpperCase())
      .join('');

  const verifiedBadge = () =>
    `<span class="dm-verified"><svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true"><circle cx="8" cy="8" r="8" fill="currentColor"/><path d="M4.8 8.2l2.1 2.1 4.3-4.4" fill="none" stroke="var(--dm-bg, #fff)" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>Achat vérifié</span>`;

  function carouselCard(r) {
    return `<article class="dm-rcard">
      ${starsHTML(r.rating, 15)}
      ${r.title ? `<h3 class="dm-rcard__title">${esc(r.title)}</h3>` : ''}
      ${r.text ? `<p class="dm-rcard__text">${esc(r.text)}</p>` : ''}
      <div class="dm-rcard__foot">
        ${r.photos[0] ? `<img class="dm-rcard__photo" src="${esc(r.photos[0])}" alt="" loading="lazy" width="48" height="48">` : `<span class="dm-avatar" aria-hidden="true">${esc(initials(r.name))}</span>`}
        <div><p class="dm-rcard__name">${esc(r.name)}</p>${r.verified ? verifiedBadge() : ''}</div>
      </div>
    </article>`;
  }

  function listItem(r, idx) {
    const long = r.text && r.text.length > 320;
    return `<article class="dm-review">
      <div class="dm-review__author">
        <span class="dm-avatar" aria-hidden="true">${esc(initials(r.name))}</span>
        <div>
          <p class="dm-review__name">${esc(r.name)}</p>
          ${r.verified ? verifiedBadge() : ''}
          ${r.location ? `<p class="dm-review__meta">${esc(r.location)}</p>` : ''}
        </div>
      </div>
      <div class="dm-review__body">
        <div class="dm-review__top">${starsHTML(r.rating, 16)}${r.date ? `<time class="dm-review__meta">${esc(r.date)}</time>` : ''}</div>
        ${r.title ? `<h3 class="dm-review__title">${esc(r.title)}</h3>` : ''}
        ${r.text ? `<p class="dm-review__text${long ? ' is-clamped' : ''}" id="dm-rt-${idx}">${esc(r.text)}</p>` : ''}
        ${long ? `<button type="button" class="dm-review__more" data-dm-more="dm-rt-${idx}">Lire la suite</button>` : ''}
        ${
          r.photos.length
            ? `<div class="dm-review__photos">${r.photos
                .map((src) => `<button type="button" class="dm-review__photo" data-dm-photo="${esc(src)}"><img src="${esc(src)}" alt="Photo client" loading="lazy" width="88" height="88"></button>`)
                .join('')}</div>`
            : ''
        }
      </div>
    </article>`;
  }

  /* ------------------------------------------------------------ carousels */
  function initCarousel(root) {
    const track = root.querySelector('[data-dm-track]');
    if (!track || root.dataset.dmCarouselReady) return;
    root.dataset.dmCarouselReady = '1';
    const prev = root.querySelector('[data-dm-prev]');
    const next = root.querySelector('[data-dm-next]');
    const step = () => {
      const card = track.children[0];
      return card ? card.getBoundingClientRect().width + parseFloat(getComputedStyle(track).columnGap || 16) : track.clientWidth;
    };
    const update = () => {
      if (prev) prev.disabled = track.scrollLeft <= 4;
      if (next) next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
    };
    prev && prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
    next && next.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
    track.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    update();
  }

  async function renderCarousels(scope = document) {
    for (const root of scope.querySelectorAll('[data-dm-reviews-carousel]')) {
      const track = root.querySelector('[data-dm-track]');
      const limit = Number(root.dataset.dmLimit) || 12;
      if (track && root.dataset.dmUseCsv === 'true' && !root.dataset.dmFilled) {
        root.dataset.dmFilled = '1';
        const csv = await csvPromise;
        const extra = csv
          .filter((r) => r.rating >= 4 && (r.text || r.title))
          .sort((a, b) => (b.photos.length ? 1 : 0) - (a.photos.length ? 1 : 0) || b.ts - a.ts)
          .slice(0, Math.max(0, limit - track.children.length));
        track.insertAdjacentHTML('beforeend', extra.map(carouselCard).join(''));
      }
      root.hidden = !track || !track.children.length ? !root.hasAttribute('data-dm-keep') : false;
      initCarousel(root);
    }
  }

  /* ------------------------------------------------------------ full list */
  async function renderLists(scope = document) {
    for (const root of scope.querySelectorAll('[data-dm-reviews-list]')) {
      if (root.dataset.dmReady) continue;
      root.dataset.dmReady = '1';
      const handle = root.dataset.dmProduct || config.product;
      const all = await dataset(handle);
      const pageSize = Number(root.dataset.dmPageSize) || 5;
      const s = stats(all);

      const summary = root.querySelector('[data-dm-summary]');
      const listEl = root.querySelector('[data-dm-list]');
      const moreBtn = root.querySelector('[data-dm-load-more]');
      const sortEl = root.querySelector('[data-dm-sort]');
      const photosEl = root.querySelector('[data-dm-photos]');
      const emptyEl = root.querySelector('[data-dm-empty]');
      const toolbar = root.querySelector('[data-dm-toolbar]');

      if (!s.count) {
        if (emptyEl) emptyEl.hidden = false;
        if (summary) summary.hidden = true;
        if (toolbar) toolbar.hidden = true;
        continue;
      }

      if (summary) {
        const bars = [5, 4, 3, 2, 1]
          .map((n) => {
            const c = s.dist[n - 1];
            const pct = s.count ? Math.round((c / s.count) * 100) : 0;
            return `<button type="button" class="dm-dist__row" data-dm-filter="${n}" aria-pressed="false"><span>${n} ★</span><span class="dm-dist__bar"><span style="width:${pct}%"></span></span><span class="dm-dist__count">${c}</span></button>`;
          })
          .join('');
        const recommend = Math.round((all.filter((r) => r.rating >= 4).length / s.count) * 100);
        summary.innerHTML = `
          <div class="dm-summary__score">
            <p class="dm-summary__avg">${fmtRating(s.avg)}<span>/5</span></p>
            ${starsHTML(s.avg, 20)}
            <p class="dm-summary__count">${s.count} ${esc(LABEL)}</p>
          </div>
          <div class="dm-dist">${bars}</div>
          <div class="dm-summary__reco"><p class="dm-summary__avg">${recommend}<span>%</span></p><p class="dm-summary__count">recommandent ce produit</p></div>`;
      }

      if (photosEl) {
        const photos = all.flatMap((r) => r.photos.map((src) => ({ src, r })));
        if (photos.length) {
          photosEl.innerHTML = photos
            .slice(0, 24)
            .map((p) => `<button type="button" class="dm-review__photo" data-dm-photo="${esc(p.src)}"><img src="${esc(p.src)}" alt="Photo client" loading="lazy" width="120" height="120"></button>`)
            .join('');
          photosEl.hidden = false;
        }
      }

      let filter = 0;
      let shown = pageSize;
      const sorted = () => {
        const mode = sortEl ? sortEl.value : 'recent';
        let list = all.filter((r) => !filter || Math.round(r.rating) === filter);
        if (mode === 'photos') list = list.filter((r) => r.photos.length);
        const byDate = (a, b) => b.ts - a.ts;
        if (mode === 'high') list.sort((a, b) => b.rating - a.rating || byDate(a, b));
        else if (mode === 'low') list.sort((a, b) => a.rating - b.rating || byDate(a, b));
        else list.sort(byDate);
        return list;
      };
      const draw = () => {
        const list = sorted();
        listEl.innerHTML = list.slice(0, shown).map(listItem).join('') || '<p class="dm-review__meta">Aucun avis pour ce filtre.</p>';
        if (moreBtn) moreBtn.hidden = shown >= list.length;
      };

      root.addEventListener('click', (event) => {
        const f = event.target.closest('[data-dm-filter]');
        if (f) {
          const n = Number(f.dataset.dmFilter);
          filter = filter === n ? 0 : n;
          root.querySelectorAll('[data-dm-filter]').forEach((b) => b.setAttribute('aria-pressed', String(Number(b.dataset.dmFilter) === filter)));
          shown = pageSize;
          draw();
        }
        const more = event.target.closest('[data-dm-more]');
        if (more) {
          const p = document.getElementById(more.dataset.dmMore);
          if (p) p.classList.toggle('is-clamped');
          more.textContent = p && p.classList.contains('is-clamped') ? 'Lire la suite' : 'Réduire';
        }
      });
      moreBtn &&
        moreBtn.addEventListener('click', () => {
          shown += pageSize;
          draw();
        });
      sortEl &&
        sortEl.addEventListener('change', () => {
          shown = pageSize;
          draw();
        });
      draw();

      injectSchema(root, all, s);
    }
  }

  /* ---------------------------------------------- Google rich results */
  function injectSchema(root, list, s) {
    if (!root.dataset.dmSchema || document.getElementById('dm-review-schema')) return;
    let base = {};
    try {
      base = JSON.parse(root.dataset.dmSchema);
    } catch (e) {
      return;
    }
    const data = Object.assign({ '@context': 'https://schema.org', '@type': 'Product' }, base, {
      aggregateRating: { '@type': 'AggregateRating', ratingValue: (Math.round(s.avg * 10) / 10).toString(), reviewCount: String(s.count), bestRating: '5', worstRating: '1' },
      review: list
        .slice()
        .sort((a, b) => b.ts - a.ts)
        .slice(0, 10)
        .map((r) => ({
          '@type': 'Review',
          author: { '@type': 'Person', name: r.name },
          reviewRating: { '@type': 'Rating', ratingValue: String(r.rating), bestRating: '5' },
          ...(r.ts ? { datePublished: new Date(r.ts).toISOString().slice(0, 10) } : {}),
          ...(r.title ? { name: r.title } : {}),
          ...(r.text ? { reviewBody: r.text } : {}),
        })),
    });
    const el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = 'dm-review-schema';
    el.textContent = JSON.stringify(data);
    document.head.appendChild(el);
  }

  /* --------------------------------------------------------- lightbox */
  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-dm-photo]');
    if (!btn) return;
    let dialog = document.getElementById('dm-lightbox');
    if (!dialog) {
      dialog = document.createElement('dialog');
      dialog.id = 'dm-lightbox';
      dialog.className = 'dm-lightbox';
      dialog.innerHTML = '<button type="button" class="dm-lightbox__close" aria-label="Fermer">×</button><img alt="Photo client">';
      dialog.addEventListener('click', (e) => {
        if (e.target === dialog || e.target.closest('.dm-lightbox__close')) dialog.close();
      });
      document.body.appendChild(dialog);
    }
    dialog.querySelector('img').src = btn.dataset.dmPhoto;
    dialog.showModal();
  });

  /* ------------------------------------------------------------- boot */
  const run = (scope) => {
    renderRatings(scope);
    renderCarousels(scope);
    renderLists(scope);
  };
  run(document);
  document.addEventListener('shopify:section:load', (event) => run(event.target));
  window.DMReviews = { dataset, stats, parseCSV };
})();
